import { useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Save, Sparkles } from "lucide-react";
import { TrainingVideoButton } from "./TrainingVideo";
import { Card, Eyebrow, Kpi, NumField, btnDark, btnLight, usDateTime } from "./ui";

const ENTITIES = [
  ["sole_prop", "Sole proprietor"], ["llc", "Single-member LLC"], ["partnership", "Partnership"],
  ["s_corp", "S-Corp"], ["c_corp", "C-Corp"],
];
const CONF = { high: "#059669", medium: "#D97706", low: "#64748B" };

export default function TaxTab({ ov, reload }) {
  const t = ov.tax;
  const [s, setS] = useState({
    entity_type: ov.settings.entity_type, filing_status: ov.settings.filing_status,
    state_rate_pct: ov.settings.state_rate_pct, home_office_sqft: ov.settings.home_office_sqft,
    reasonable_salary_pct: ov.settings.reasonable_salary_pct,
  });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/advisory/settings", {
        ...s, state_rate_pct: Number(s.state_rate_pct) || 0, home_office_sqft: Number(s.home_office_sqft) || 0,
        reasonable_salary_pct: Number(s.reasonable_salary_pct) || 50,
      });
      toast.success("Tax profile saved");
      await reload();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const runAi = async () => {
    setAiBusy(true);
    try {
      await api.post("/advisory/tax/ai");
      toast.success("AI review complete");
      await reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "AI review failed"); }
    finally { setAiBusy(false); }
  };
  const select = "mt-1 w-full px-3 py-2 border bg-white text-sm";

  return (
    <div className="space-y-5">
      <div className="flex justify-end -mb-2"><TrainingVideoButton video="tax" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="tax-kpi-net-income" label="Net income (12 mo)" value={fmtMoney(t.net_income_12m)} />
        <Kpi testid="tax-kpi-estimated" label="Est. total tax" value={fmtMoney(t.estimated.total)} sub={`Effective ${t.effective_rate_pct}% · marginal ${t.marginal_rate_pct}%`} />
        <Kpi testid="tax-kpi-savings" label="Savings identified" value={fmtMoney(t.strategy_savings_total)} tone="good" sub={`${t.strategies.length} strategies`} />
        <Kpi testid="tax-kpi-quarterly" label="Quarterly estimate" value={fmtMoney(t.quarterly_estimate)} sub={`Due ${t.quarterly_due.join(", ")}`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-4" testid="tax-profile-card">
          <Eyebrow>Tax profile</Eyebrow>
          <div className="font-display font-bold text-lg mb-3">US federal + state</div>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Entity type</span>
              <select value={s.entity_type} onChange={(e) => setS({ ...s, entity_type: e.target.value })} className={select} style={{ borderColor: "var(--ll-border)" }} data-testid="tax-entity-select">
                {ENTITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Filing status</span>
              <select value={s.filing_status} onChange={(e) => setS({ ...s, filing_status: e.target.value })} className={select} style={{ borderColor: "var(--ll-border)" }} data-testid="tax-filing-select">
                <option value="single">Single</option><option value="mfj">Married filing jointly</option>
              </select>
            </label>
            <NumField label="State income tax rate" suffix="%" step="0.1" min="0" value={s.state_rate_pct} onChange={(v) => setS({ ...s, state_rate_pct: v })} testid="tax-state-rate" />
            <NumField label="Home office (sq ft, max 300)" step="10" min="0" value={s.home_office_sqft} onChange={(v) => setS({ ...s, home_office_sqft: v })} testid="tax-home-office" />
            <NumField label="S-Corp reasonable salary" suffix="% of profit" step="5" min="10" value={s.reasonable_salary_pct} onChange={(v) => setS({ ...s, reasonable_salary_pct: v })} testid="tax-salary-pct" />
            <button onClick={save} disabled={busy} className={`${btnDark} w-full justify-center`} data-testid="tax-profile-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save & recalculate
            </button>
          </div>
          <div className="mt-4 text-xs space-y-1 font-mono-fin" data-testid="tax-breakdown">
            <div className="flex justify-between"><span>Self-employment tax</span><span>{fmtMoney(t.estimated.se_tax)}</span></div>
            <div className="flex justify-between"><span>Federal income tax</span><span>{fmtMoney(t.estimated.federal)}</span></div>
            <div className="flex justify-between"><span>State ({t.state_rate_pct}%)</span><span>{fmtMoney(t.estimated.state)}</span></div>
          </div>
        </Card>
        <div className="lg:col-span-8 space-y-5">
          <Card testid="tax-strategies-card">
            <Eyebrow>Calculated strategies</Eyebrow>
            <div className="font-display font-bold text-lg mb-3">Ways to lower your tax bill</div>
            {t.strategies.length === 0 && <div className="text-sm text-[var(--ll-ink-soft)]">No standard strategies apply at this income level.</div>}
            <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {t.strategies.map((x) => (
                <li key={x.key} className="py-3 flex items-start justify-between gap-4" data-testid={`tax-strategy-${x.key}`}>
                  <div><div className="font-medium text-sm">{x.title}</div><div className="text-xs text-[var(--ll-ink-soft)] mt-1">{x.detail}</div></div>
                  <div className="font-mono-fin text-sm font-bold text-[var(--ll-success)] whitespace-nowrap shrink-0 pr-1">~{fmtMoney(x.est_savings)}/yr</div>
                </li>
              ))}
            </ul>
          </Card>
          <Card testid="tax-ai-card">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <Eyebrow>AI review of your spending</Eyebrow>
                <div className="font-display font-bold text-lg">More opportunities</div>
                {ov.tax_ai?.created_at && <div className="text-[11px] text-[var(--ll-ink-muted)]">Last run {usDateTime(ov.tax_ai.created_at)}</div>}
              </div>
              <button onClick={runAi} disabled={aiBusy} className={btnLight} style={{ borderColor: "var(--ll-border)" }} data-testid="tax-ai-run">
                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {ov.tax_ai ? "Re-run AI review" : "Run AI review"}
              </button>
            </div>
            {!ov.tax_ai && <div className="text-sm text-[var(--ll-ink-soft)]">Let AI scan your 12-month categories for missed write-offs (equipment, vehicle, health insurance, credits…).</div>}
            <ul className="space-y-3">
              {(ov.tax_ai?.items || []).map((x, i) => (
                <li key={i} className="border p-3" style={{ borderColor: "var(--ll-border)" }} data-testid={`tax-ai-item-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-sm">{x.title}</div>
                    <div className="text-right whitespace-nowrap">
                      {x.est_savings != null && <div className="font-mono-fin text-sm font-bold text-[var(--ll-success)]">~{fmtMoney(x.est_savings)}</div>}
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: CONF[x.confidence] }}>{x.confidence} confidence</div>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--ll-ink-soft)] mt-1">{x.detail}</div>
                </li>
              ))}
            </ul>
          </Card>
          <div className="text-[11px] text-[var(--ll-ink-muted)]" data-testid="tax-disclaimer">
            Estimates use {t.rates_year} US federal brackets, standard deduction, SE tax and a simplified 20% QBI deduction, plus your state rate. Educational only — not tax advice. Confirm with your CPA.
          </div>
        </div>
      </div>
    </div>
  );
}
